import { capture } from './capture';

/**
 * Where a report-create flow was entered from (BIL-5324). Closed enum on
 * purpose: it is an event property, so it can never carry a route, an id or
 * free text. See docs/product-analytics-contract.md §"Report entry points".
 */
export const REPORT_ENTRY_POINTS = [
  'footer_sheet',
  'dashboard_quick_action',
  'machine_page',
  'qr_scan',
  'qr_search',
  'pwa_shortcut',
  'qr_sticker',
  'direct',
] as const;

export type ReportEntryPoint = (typeof REPORT_ENTRY_POINTS)[number];

export const isReportEntryPoint = (value: unknown): value is ReportEntryPoint =>
  typeof value === 'string' &&
  (REPORT_ENTRY_POINTS as readonly string[]).includes(value);

/** `location.state` key senders use: `history.push(url, { [KEY]: 'qr_scan' })`. */
export const ANALYTICS_ENTRY_POINT_STATE_KEY = 'analyticsEntryPoint';
/** Query-string key for entries that cannot carry state (the PWA shortcut). */
export const ANALYTICS_ENTRY_QUERY_KEY = 'entry';

const readStateEntry = (state: unknown): unknown =>
  typeof state === 'object' && state !== null
    ? (state as Record<string, unknown>)[ANALYTICS_ENTRY_POINT_STATE_KEY]
    : undefined;

/** state value if valid, else the `entry` query param if valid, else `direct`. */
export const resolveReportEntryPoint = ({
  state,
  search,
}: {
  state?: unknown;
  search?: string;
}): ReportEntryPoint => {
  const fromState = readStateEntry(state);
  if (isReportEntryPoint(fromState)) return fromState;
  const fromQuery = new URLSearchParams(search ?? '').get(
    ANALYTICS_ENTRY_QUERY_KEY
  );
  if (isReportEntryPoint(fromQuery)) return fromQuery;
  return 'direct';
};

// The entry point of the report-create screen currently mounted, read by the
// analytics link when the user submits (mutation_completed.entry_point).
// Module-level on purpose: the Apollo link chain has no React access.
let pendingReportEntryPoint: ReportEntryPoint | null = null;

export const setPendingReportEntryPoint = (
  entry: ReportEntryPoint | null
): void => {
  pendingReportEntryPoint = entry;
};

export const getPendingReportEntryPoint = (): ReportEntryPoint | null =>
  pendingReportEntryPoint;

/** One `report_started` per mount of a report-create screen. */
export const captureReportStarted = ({
  entryPoint,
  reportType,
  machinePreselected,
}: {
  entryPoint: ReportEntryPoint;
  reportType: string;
  machinePreselected: boolean;
}): void => {
  capture('report_started', {
    entry_point: entryPoint,
    report_type: reportType,
    machine_preselected: machinePreselected,
  });
};
